import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';

import { ReactComponent as FacilityIcon } from '../../assets/icons/device-management/device-icon.svg';
import { ReactComponent as DodIcon } from '../../assets/icons/dot-circle.svg';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus.svg';
import DefaultApiLimit from '../../config/defaultLimit';
import { powerUnit } from '../../config/enum';
import {
  AvailableDevice,
  RequestDeviceInterFace,
} from '../../interface/device-management.interface';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  fetchAllDevices,
  fetchListFilters,
  setDeviceFilterData,
  setSortData,
  updateDeviceFilters,
} from '../../redux/slice/DeviceManagementSlice';
import { setNotificationDetails } from '../../redux/slice/NotificationSlice';
import { setPageTitle } from '../../redux/slice/PageTitleSlice';
import { setSelectedStation } from '../../redux/slice/StationManagementSidePanelSlice';
import { RootState } from '../../redux/store';
import MyButton from '../../shared-components/button/Button';
import CustomTable from '../../shared-components/custom-table/CustomTable';
import DisableSwitchWarning from '../../shared-components/disable-warning/DisableWarning';
import ModalBox from '../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../shared-components/success-notification/SuccessNotification';
import SwitchButton from '../../shared-components/switch-button-field/SwitchButton';
import DeviceSidePanel from '../../shared-components/table-side-panel/DeviceSidePanel';
import StationViewSidePanel from '../../shared-components/table-side-panel/StationViewSidePanel';
import { GetParametersFromCommonFilters } from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DataTime';
import { exportCSV, exportPDF } from '../../shared-functions/ExportFile';
import deviceApi from '../../utils/api/device';

import AddDevice from './components/AddDevice';

interface ModalBoxDetail {
  title: string | null;
  header: boolean;
  content: JSX.Element | null;
  notificationModal: boolean;
}

interface SubscribersConnectors {
  userConnecters: number;
  companyConnecterLimit: number;
}

/**
 * Device management
 *
 */
function DeviceManagement() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation('deviceManagement');
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [searchParameters, setSearchParameters] = useSearchParams();
  const { page: pageParameter } = useParams<{ page: string }>();
  const [urlFilterApplied, setUrlFilterApplied] = useState(false);
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    header: false,
    content: null,
    notificationModal: false,
  });
  const [subscribersConnectors, setSubscribersConnectors] = useState<SubscribersConnectors | null>(
    null,
  );
  const {
    devices,
    page,
    limit,
    totalPages,
    totalResults,
    loading,
    filters,
    filterListLoading,
    sortBy,
    sortField,
  } = useAppSelector((states: RootState) => states.device);
  const { currentFacilityTagsList } = useAppSelector((state: RootState) => state.facilityTags);
  const [sidePanelOpen, setSidePanelOpen] = useState<boolean>(false);

  /**
   * Handle the fetch data for Api call function
   * @param data - RequestDeviceInterFace
   */
  const fetchData = (data: RequestDeviceInterFace) => {
    const selectedFacility = currentFacilityTagsList.find((item) => item.isSelected);

    if (selectedFacility?._id && !loading) {
      dispatch(
        fetchAllDevices({
          ...data,
        }),
      );
    }
  };

  /**
   * Fetch subscriber connector limit and used value
   */
  const fetchSubscriptionConnectors = () => {
    deviceApi
      .getSubscriptionConnecterLimit()
      .then((response) => {
        if (response?.data) {
          setSubscribersConnectors({
            userConnecters: response.data?.userConnecters ?? 0,
            companyConnecterLimit: response.data?.companyConnecterLimit ?? 0,
          });
        }
      })
      .catch(() => {
        setSubscribersConnectors(null);
      });
  };

  /**
   * Function to handle data limit change
   */
  const handleLimitChange = (changeLimit: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page: DefaultApiLimit.page,
      limit: changeLimit,
      sortField,
      sortBy,
    });
  };

  /**
   * Handles the pagination
   * @param changePage - The page number to navigate to
   */
  const handlePageChange = (changePage: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      sortField,
      sortBy,
      page: changePage,
      limit,
    });
  };

  /**
   * Handle the filter data clear function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      setDeviceFilterData({
        ...sortData,
      }),
    );
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Updates the availability status of a device in the local state
   * @param {string} deviceId - The ID of the device to update
   * @param {boolean} isAvailable - The new availability status
   */
  const updateAvailability = (deviceId: string, isAvailable: boolean) => {
    deviceApi
      .editAvailableStatusDevice(deviceId, { availabilityStatus: isAvailable })
      .then(() => {
        setModalBoxStatus('open');
        setModalBoxDetail({
          title: '',
          header: false,
          content: (
            <SuccessNotification
              title={t('statusChangedSuccess')}
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={() => {
                setModalBoxStatus('close');
                fetchData({
                  page,
                  limit, // Need to fetch the current page
                });
              }}
            />
          ),
          notificationModal: true,
        });
      })
      .catch((error) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  /**
   * Handles the availability status change for a device
   * Shows a confirmation modal when disabling a device
   * @param {AvailableDevice} device - The device to update
   * @param {boolean} checked - The new availability status
   */
  const handleAvailabilityChange = (device: AvailableDevice, checked: boolean) => {
    if (checked) {
      updateAvailability(device._id, checked);
    } else {
      setModalBoxStatus('open');
      setModalBoxDetail({
        title: '',
        header: false,
        content: (
          <DisableSwitchWarning
            selectedId={device._id}
            onCancel={() => setModalBoxStatus('close')}
            message={t('disableDeviceWarning')}
            onConfirm={() => updateAvailability(device._id, checked)}
          />
        ),
        notificationModal: true,
      });
    }
  };

  /**
   * Renders the custom data for the availability column
   * @param {AvailableDevice} device - The device data
   */
  const renderAvailability = (device: AvailableDevice) => (
    <div
      role="presentation"
      className="flex justify-center"
      onClick={(even) => even.stopPropagation()}>
      <div key={device.chargingStation}>
        <SwitchButton
          checked={device.isAvailable}
          change={(checked: boolean) => handleAvailabilityChange(device, checked)}
        />
      </div>
    </div>
  );

  /**
   * UI of Status
   * @param device - The device data
   */
  const renderStatus = (device: AvailableDevice) =>
    device.isDeviceOffline !== undefined ? (
      <div>
        <div className={device.isDeviceOffline ? 'tag-danger-label' : 'tag-success-label'}>
          <DodIcon />
          <span style={{ fontWeight: '700' }}>
            {device.isDeviceOffline ? t('offline') : t('online')}
          </span>
        </div>
      </div>
    ) : (
      <div />
    );

  /**
   * Function to open device view model
   */
  const handleOpenModelView = (chargingStationDate: AvailableDevice, newTab: boolean) => {
    if (chargingStationDate) {
      dispatch(
        setSelectedStation({
          id: chargingStationDate?.chargingStationId,
          name: chargingStationDate?.chargingStation,
        }),
      );
      if (newTab) {
        const url = `/ev-admin/device-management/station/${chargingStationDate.chargingStationId}/station-dashboard?searchBy=${chargingStationDate.chargingStation}`;
        window.open(url, '_blank'); // opens in a new tab
      } else {
        navigate({
          pathname: `/ev-admin/device-management/station/${chargingStationDate.chargingStationId}/station-dashboard`,
          search: `?searchBy=${chargingStationDate.chargingStation}`,
        });
      }
    } else {
      console.log('Error while opening device in separate page');
    }
  };

  const column = [
    {
      label: t('stationName'),
      accessorKey: 'chargingStation' as keyof AvailableDevice,
      sortAllow: true,
      queryParamsKey: 'stationId',
      queryParamsValue: 'chargingStationId',
      sidePanelComponent: <StationViewSidePanel fetchIdFrom="queryParams" />,
      handleClickExpandView: handleOpenModelView,
    },
    {
      label: t('deviceName'),
      accessorKey: 'chargingPointName' as keyof AvailableDevice,
      // queryParamsKey: 'deviceId',
      // sidePanelComponent: <DeviceSidePanel fetchIdFrom="queryParams" />,
      sortAllow: true,
    },
    {
      label: t('deviceModel'),
      accessorKey: 'chargePointModel' as keyof AvailableDevice,
      hideWhenSidePanelOpen: true,
    },
    {
      label: t('deviceId'),
      accessorKey: 'chargingPointSn' as keyof AvailableDevice,
    },
    {
      label: t('maxPowerOutput'),
      accessorKey: 'maximumOutputPower' as keyof AvailableDevice,
      sortAllow: true,
      /**
       * Data UI in table
       * @param device - The device data
       */
      customData: (device: AvailableDevice) => (
        <div className="value-container">
          {device?.maximumOutputPower && (
            <>
              <p className="value">{device.maximumOutputPower}</p>
              <span>{device.maximumOutputPowerUnit === 'A' ? powerUnit.A : powerUnit.KWH}</span>
            </>
          )}
        </div>
      ),
    },
    {
      label: t('desiredPowerOutput'),
      accessorKey: 'desiredPowerOutput' as keyof AvailableDevice,
      sortAllow: true,
      /**
       * Data UI in table
       * @param device - The device data
       */
      customData: (device: AvailableDevice) => (
        <div className="value-container">
          {device?.chargeRate && (
            <>
              <p className="value">
                {device.chargeRate.unit === 'W'
                  ? device.chargeRate.value / 1000
                  : device.chargeRate.value}
              </p>
              <span>{device.chargeRate.unit === 'W' ? powerUnit.KWH : powerUnit.A}</span>
            </>
          )}
        </div>
      ),
    },
    // {
    //   label: 'LBC',
    //   accessorKey: 'lbc' as keyof AvailableDevice,
    // },
    {
      label: t('connector1'),
      accessorKey: 'connector1' as keyof AvailableDevice,
      sortAllow: false,
    },
    {
      label: t('connector2'),
      accessorKey: 'connector2' as keyof AvailableDevice,
      sortAllow: false,
    },
    {
      label: t('availability'),
      accessorKey: 'isAvailable' as keyof AvailableDevice,
      customData: renderAvailability,
      hideWhenSidePanelOpen: true,
    },
    {
      label: t('deviceStatus'),
      accessorKey: 'isDeviceOffline' as keyof AvailableDevice,
      customData: renderStatus,
      sortAllow: false,
      hideWhenSidePanelOpen: true,
    },
  ];

  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('stationName'), accessorKey: 'chargingStation' },
    { label: t('deviceName'), accessorKey: 'chargingPointName' },
    { label: t('deviceModel'), accessorKey: 'chargePointModel' },
    { label: t('deviceId'), accessorKey: 'chargingPointSn' },
    { label: t('maxPowerOutput'), accessorKey: 'maximumOutputPower' },
    { label: t('desiredPowerOutput'), accessorKey: 'desiredPowerOutput' },
    { label: t('connector1'), accessorKey: 'connector1' },
    { label: t('connector2'), accessorKey: 'connector2' },
    { label: t('availability'), accessorKey: 'isAvailable' },
    { label: t('deviceStatus'), accessorKey: 'isDeviceOffline' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);
    deviceApi
      .fetchDevicesExport(
        removeEmptyKeys({
          ...queryParameters,
          sortBy,
          sortField,
        }),
      )
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.results.map((row: AvailableDevice, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'maximumOutputPower':
                return `${row?.maximumOutputPower} ${row?.maximumOutputPowerUnit === 'A' ? powerUnit.A : powerUnit.KWH}`;
              case 'desiredPowerOutput':
                return `${
                  row?.chargeRate?.unit === 'W'
                    ? `${row.chargeRate.value / 1000} ${powerUnit.KWH}`
                    : `${row?.chargeRate?.value} ${powerUnit.A}`
                }`;
              case 'connector1':
                return `${row?.connectorsStatus?.connector1 || 'N/A'}`;
              case 'connector2':
                return `${row?.connectorsStatus?.connector2 || 'N/A'}`;
              case 'isAvailable':
                return `${row?.isAvailable ? 'Available' : 'Unavailable'}`;
              case 'isDeviceOffline':
                return `${row?.isDeviceOffline ? 'Offline' : 'Online'}`;
              default:
                return (row as any)[col?.accessorKey] || 'N/A';
            }
          }),
        );
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'devices-list.csv');
          } else {
            exportPDF(headers, rows, 'devices-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortBy, sortField });
  };

  /**
   * Handle the open side panel on row click function
   * @param _ - The row id
   * @param row - The device data
   */
  const handleOpenSidePanelOnRowClick = (_: string, row: AvailableDevice) => {
    if (!row?._id) return;

    // remove station panel if open
    if (searchParameters.has('stationId')) {
      searchParameters.delete('stationId');
    }

    // open device panel
    searchParameters.set('deviceId', row._id);
    setSearchParameters(searchParameters);

    dispatch(
      setPageTitle({
        title: 'Chargers',
        icon: <FacilityIcon />,
        count: totalResults,
        subscribersConnectors: subscribersConnectors || undefined,
      }),
    );
  };

  useEffect(() => {
    if (!urlFilterApplied) return;

    const queryParameters = GetParametersFromCommonFilters(filters);

    fetchData({
      ...queryParameters,
      page,
      limit,
    });
  }, [currentFacilityTagsList, filters, urlFilterApplied]);

  useEffect(() => {
    fetchSubscriptionConnectors();
  }, [currentFacilityTagsList]);

  useEffect(() => {
    if (searchParameters.get('deviceId')) {
      const parameters = new URLSearchParams(searchParameters);
      parameters.delete('deviceId');
      setSearchParameters(parameters, { replace: true });
    }
  }, []);

  useEffect(() => {
    if (!sidePanelOpen) {
      dispatch(
        setPageTitle({
          title: t('chargers'),
          icon: <FacilityIcon />,
          count: totalResults,
          subscribersConnectors: subscribersConnectors || undefined,
        }),
      );
    }
  }, [totalResults, sidePanelOpen, subscribersConnectors]);

  useEffect(() => {
    if (pageParameter === 'add') {
      setSidePanelOpen(true);
    }
  }, [pageParameter]);

  useEffect(() => {
    const status = searchParameters.get('connectorStatus');

    if (status) {
      dispatch(
        updateDeviceFilters({
          key: 'connectorStatus',
          value: status,
          type: 'restAndAdd',
        }),
      );
    }

    setUrlFilterApplied(true);
  }, []);

  return (
    <div className="device-management-wrap">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={modalBoxDetail.header}
      />
      <div className="table-container">
        <CustomTable
          dataKey="DeviceManagement"
          paginate
          loading={loading}
          limit={limit}
          header={column}
          data={devices}
          currentPage={page}
          totalPages={totalPages}
          totalResults={totalResults}
          sortData={{
            sortBy,
            sortField,
          }}
          handleSortChange={handleSortFunction}
          handlePageChange={handlePageChange}
          handleLimitChange={handleLimitChange}
          exportDataPdfOrCSV={exportDataPdfOrCSV}
          exportLoader={exportLoader}
          topHeader={false} // Hide existing filters
          filters={filters}
          filtersToDispatch={updateDeviceFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListFilters}
          refreshRequired
          refreshCurrentPages={handleRefresh}
          hoverLabel={t('refresh')}
          serialNo
          flexDirection
          onHiddenColumnsChange={setHiddenColumns}
          rowClickEvent={handleOpenSidePanelOnRowClick}
          rowClickParamsKey="deviceId"
          rowClickSidePanel={<DeviceSidePanel fetchIdFrom="queryParams" />}
          sidePanelEnabled={sidePanelOpen}
          sidePanelManualContent={<AddDevice setSidePanelOpen={setSidePanelOpen} />}
          onSidePanelClose={() => {
            const parameters = new URLSearchParams(searchParameters);
            parameters.delete('facilityId');
            setSearchParameters(parameters);

            setSidePanelOpen(false);
          }}
          leftTopContent={
            <div className="table-bottom-button-list">
              <div className="table-button-item">
                <MyButton
                  label={t('addDevice')}
                  buttonType="submit"
                  onClickFunc={() => setSidePanelOpen(true)}
                  svgIcon={<PlusIcon />}
                />
              </div>
            </div>
          }
        />
      </div>
    </div>
  );
}
export default DeviceManagement;
